import { useTokenizedApi } from '../useTokenizedApi';
import { useQueries, useQuery } from 'react-query';
import { Instrument, TInstrumentDescription } from '@tokenized/sdk-js-private';
import { usePersonalProfile } from '../entities/usePersonalProfile';
import { useProfile } from '../entities/useProfile';
import { useIntl } from '../intl/useIntl';
import { useStaticResources } from '../staticResources/useStaticResources';

/** @category Instruments */
export function useInstrumentListWithDetails(
  instrumentDescriptions: TInstrumentDescription[],
  profileId?: string,
) {
  const tokenizedApi = useTokenizedApi();
  const intl = useIntl();

  const personalProfile = usePersonalProfile();
  profileId = profileId || personalProfile?.id;
  const displayCurrencyCode = useProfile(profileId)?.displayCurrency;
  const currencies = useStaticResources()?.currencies;

  let instruments: Instrument[] = [];
  for (const options of instrumentDescriptions || []) {
    instruments.push(
      new Instrument(options)
        .withIntl(intl)
        .withCurrenciesStaticResources(currencies),
    );
  }

  // This allows fully-functional Instrument objects to be constructed from
  // contract instrument draft IDs (the instrument details are extracted
  // from contract draft data in the query cache)
  instruments = instruments.map((instrument) =>
    tokenizedApi.contracts.setInstrumentDetailsFromContractDraft(instrument),
  );

  const instrumentDetails = useQueries(
    instruments.map((instrument) =>
      tokenizedApi.treasury.instrumentDetailsQuery(
        instrument.instrumentIdToQuery,
      ),
    ),
  ).map((result) => result?.data);
  instruments = instruments.map((instrument, index) =>
    instrument.withInstrumentDetails(instrumentDetails[index]),
  );

  const exchangeRates = useQuery(
    tokenizedApi.treasury.allExchangeRatesQuery(),
  ).data;

  instruments = instruments.map((instrument) =>
    instrument.withDisplayCurrencyRate(
      displayCurrencyCode,
      exchangeRates?.findRate(
        instrument.exchangeRateToQuery,
        displayCurrencyCode,
      ),
    ),
  );

  return instruments;
}
